// Same store, key and entry shape search-insights uses for `inferQueryID`
// (utils/objectQueryTracker): `${index}_${objectID}` -> [queryID, timestamp].
const QUERY_CACHE_KEY = 'AlgoliaObjectQueryCache';

type QueryCache = Record<string, [queryID: string, timestamp: number]>;

const cacheKey = (index: string, objectID: string) => `${index}_${objectID}`;

const readCache = (): QueryCache => {
  try {
    const cache: unknown = JSON.parse(
      localStorage.getItem(QUERY_CACHE_KEY) ?? '{}',
    );
    return cache && typeof cache === 'object' && !Array.isArray(cache)
      ? (cache as QueryCache)
      : {};
  } catch {
    return {};
  }
};

const writeCache = (cache: QueryCache) => {
  try {
    localStorage.setItem(QUERY_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Storage unavailable or full: attribution falls back to no queryID.
  }
};

/**
 * Gives `toObjectID` the queryID cached for `fromObjectID`, unless it has a more
 * recent one, so a conversion on another variant of a product found by a
 * search is still attributed to that search.
 */
export const carryCachedQueryID = (
  index: string,
  fromObjectID: string,
  toObjectID: string,
) => {
  if (fromObjectID === toObjectID) return;

  const cache = readCache();
  const entry = cache[cacheKey(index, fromObjectID)];
  const target = cache[cacheKey(index, toObjectID)];
  if (!entry?.[0] || (target?.[0] && target[1] >= entry[1])) return;

  cache[cacheKey(index, toObjectID)] = entry;
  writeCache(cache);
};

/**
 * Drops the queryID cached for a product, so a later conversion is not
 * attributed to an older search once the product was reached another way.
 */
export const forgetCachedQueryID = (index: string, objectID: string) => {
  const cache = readCache();
  if (!(cacheKey(index, objectID) in cache)) return;

  delete cache[cacheKey(index, objectID)];
  writeCache(cache);
};
